/**
 * Rule-finder helpers for the Guardrails rules list.
 *
 * The Guardrails view groups rules into seven categories (agent identity,
 * rate limits, spend caps, scope, dark patterns, prompt injection, custom
 * DSL). Rule IDs (r01…r31, r_custom) were assigned chronologically, so
 * within a category they are non-sequential — Rate limits spans r03, r04,
 * r13, r24, r30. The founder reported (2026-10-29) that finding a known
 * rule by number means clicking through every category tab in turn.
 *
 * Rule IDs are permanent (they appear in signed audit records and saved
 * policies), so this file fixes the finding problem in the UI only:
 *
 *   1. A "Find a rule" search input accepts rule-number variants
 *      ("r26", "26", "R26", "r6"→r06, "r_custom", "custom") plus
 *      case-insensitive substring matches on rule name and description.
 *   2. A "By category | By number" segmented control switches between the
 *      existing category-tab layout and a flat list sorted numerically.
 *      The choice persists per viewer in localStorage.
 *   3. In both modes, rows render their rule number; in "By number"
 *      mode (and in cross-category search results) each row also carries
 *      a small mono category label so an operator never loses context.
 */

import { useCallback, useState } from 'react';
import { Search, X } from 'lucide-react';

import type { Rule, RuleType } from '@sill/types';

import type { ThemeTokens } from '@sill/ui';

import { categoryById, ruleByType, type RuleDef } from './catalog.js';
import {
  matchStandardsOnRule,
  ruleHasStandardMatch,
  type StandardRef,
} from './rule-standards.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type RuleFinderMode = 'category' | 'number';

const MODE_STORAGE_KEY = 'sill.guardrails.ruleFinderMode';

/**
 * Read the stored view mode from localStorage, swallowing every failure
 * (quota exceeded, SecurityError in a sandboxed iframe, "localStorage is
 * undefined" in SSR). An unreadable or missing value → 'category'.
 */
export function readStoredMode(): RuleFinderMode {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'category';
    const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
    return raw === 'number' ? 'number' : 'category';
  } catch {
    return 'category';
  }
}

/** Persist the mode; failures (quota, SecurityError, …) are swallowed. */
export function writeStoredMode(mode: RuleFinderMode): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore — a dashboard user without localStorage still gets a
       working finder, just without the persisted preference. */
  }
}

export interface RuleFinderState {
  readonly query: string;
  readonly mode: RuleFinderMode;
  setQuery(next: string): void;
  setMode(next: RuleFinderMode): void;
  clearQuery(): void;
}

/**
 * Query + view-mode state for the rules catalog. The mode persists per
 * viewer in localStorage; the query is session-local (clears on reload,
 * matching the AttackersView / Agents search inputs — a search query is
 * a transient filter, not a saved preference).
 */
export function useRuleFinder(): RuleFinderState {
  const [query, setQuery] = useState('');
  const [mode, setModeState] = useState<RuleFinderMode>(() => readStoredMode());
  const setMode = useCallback((next: RuleFinderMode) => {
    setModeState(next);
    writeStoredMode(next);
  }, []);
  const clearQuery = useCallback(() => setQuery(''), []);
  return { query, mode, setQuery, setMode, clearQuery };
}

// ---------------------------------------------------------------------------
// Matching + sorting primitives (pure, unit-testable).
// ---------------------------------------------------------------------------

/**
 * Extract the numeric portion of a rule type (`'r26'` → 26, `'r_custom'`
 * → null). The DSL escape valve has no number; non-numeric suffixes sort
 * after every numbered rule.
 */
export function extractRuleNumber(type: RuleType | string): number | null {
  const m = /^r0*(\d+)$/i.exec(type);
  if (m === null || m[1] === undefined) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalise a user-typed query into one of three shapes:
 *   - `{ kind: 'number', value: n }` — numeric variant ("r26", "26", "R26",
 *     "r6" all map to `{ kind: 'number', value: 6 or 26 }`).
 *   - `{ kind: 'custom' }` — the user typed "r_custom" or "custom" exactly.
 *   - `{ kind: 'none' }` — the query is empty or non-id-shaped (fall back
 *     to substring search on name / description).
 *
 * Returning the parsed number instead of a padded string keeps the match
 * robust to future rule-id widths (r26 vs. r306) and lets a two-digit
 * query like "6" match "r06" without ambiguity.
 */
export type QueryShape =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'custom' }
  | { readonly kind: 'none' };

export function parseIdQuery(raw: string): QueryShape {
  const q = raw.trim().toLowerCase();
  if (q === '') return { kind: 'none' };
  if (q === 'r_custom' || q === 'custom') return { kind: 'custom' };
  // Accept "r26", "26", "R26", "r6", "r06". The optional 'r' prefix lets
  // "26" land on r26 even without the prefix the operator sometimes drops.
  const m = /^r?0*(\d+)$/.exec(q);
  if (m !== null && m[1] !== undefined) {
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n)) return { kind: 'number', value: n };
  }
  return { kind: 'none' };
}

/**
 * True when `rule` matches `query`. The query is interpreted as either:
 *   - a RULE-ID lookup — if the trimmed query parses as a number variant
 *     ("r26", "26", "R26", "r6" → r06) or as the DSL escape valve
 *     ("r_custom", "custom"), the match is EXACT on `rule.type`. No
 *     substring fallback: otherwise "6" would land on both r06 and r26
 *     (because the rule_id string "r26" contains "6") and "find a rule by
 *     number" would mean "guess which rule has the digit in it".
 *   - a FREE-TEXT lookup — any other query substring-matches
 *     case-insensitively against the rule's display name, its
 *     description, and its raw rule_id (so a merchant-chosen slug like
 *     "holiday_hitl" is findable too). The catalog-provided name /
 *     description are used when the rule itself does not carry its own
 *     (fresh policy drafts omit the ADR-0030 §1.1 metadata).
 *
 * Empty / whitespace-only queries match every rule (identity).
 */
export function matchesRule(rule: Rule, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;
  const shape = parseIdQuery(trimmed);
  if (shape.kind === 'number') {
    return extractRuleNumber(rule.type) === shape.value;
  }
  if (shape.kind === 'custom') {
    return rule.type === 'r_custom';
  }
  // Substring fallback — case-insensitive against name + description +
  // the raw rule_id so a merchant-chosen slug ("holiday-cap") can be
  // located alongside catalog metadata.
  const needle = trimmed.toLowerCase();
  const catalog: RuleDef | undefined = ruleByType(rule.type);
  const name = (rule.display_name ?? catalog?.name ?? '').toLowerCase();
  const desc = (rule.description ?? catalog?.description ?? '').toLowerCase();
  const id = String(rule.rule_id).toLowerCase();
  return name.includes(needle) || desc.includes(needle) || id.includes(needle);
}

/**
 * Sort rules by their numeric rule-id ascending. Non-numeric types
 * (`r_custom`, future DSL escape valves) sort stably after every
 * numbered rule. Pure — never mutates the input.
 */
export function sortRulesByNumber<T extends Rule>(rules: readonly T[]): T[] {
  return [...rules].sort((a, b) => {
    const na = extractRuleNumber(a.type);
    const nb = extractRuleNumber(b.type);
    if (na === null && nb === null) {
      // Fall back to rule_id compare so the order is deterministic.
      return String(a.rule_id).localeCompare(String(b.rule_id));
    }
    if (na === null) return 1;
    if (nb === null) return -1;
    return na - nb;
  });
}

/**
 * True when `rdef` matches `query`, using the SAME semantics as
 * `matchesRule`: an id-shaped query is an EXACT match on `rdef.type`
 * ("r26", "26", "R26", "r6"→r06, "r_custom"/"custom"); otherwise a
 * case-insensitive substring on name / description / type FALLS BACK
 * to a security-standard match against the rule's canonical mapping
 * (OWASP LLM / Agentic, MITRE ATLAS, NIST, AP2, CMA, DMCC — see
 * `rule-standards.ts`).
 *
 * Standards matching is additive: a query like "OWASP" is a substring
 * fallback that would otherwise fail the name/desc/type check — the
 * standards layer catches it and surfaces every rule mapped to any
 * OWASP family. Likewise for id-shaped standard tokens ("LLM01",
 * "ASI03", "T0139", "AML.T0139", "API4") that are also substring-
 * findable on the standard id.
 *
 * Catalog defs don't carry a merchant-chosen `rule_id`, so this helper
 * is the Add-rule-popup counterpart to `matchesRule`.
 */
export function matchesRuleDef(rdef: RuleDef, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;
  const shape = parseIdQuery(trimmed);
  if (shape.kind === 'number') {
    return extractRuleNumber(rdef.type) === shape.value;
  }
  if (shape.kind === 'custom') {
    return rdef.type === 'r_custom';
  }
  const needle = trimmed.toLowerCase();
  const name = (rdef.name ?? '').toLowerCase();
  const desc = (rdef.description ?? '').toLowerCase();
  const type = rdef.type.toLowerCase();
  if (name.includes(needle) || desc.includes(needle) || type.includes(needle)) {
    return true;
  }
  // Fall through to the per-rule standards map. Returns true for
  // framework aliases ("owasp", "mitre", "nist", "ap2") and canonical
  // id substrings ("LLM01", "AML.T0139", "T0139", "API4"). A rule with
  // no standards returns false — never match-everything.
  return ruleHasStandardMatch(rdef.type, trimmed);
}

/**
 * True when the match on `rdef` for `query` is coming EXCLUSIVELY from
 * the standards layer — i.e. the query did not substring-match name,
 * description, or type, but DID resolve against a mapped standard. The
 * popup finder uses this to decide whether to render a "Matches: …"
 * annotation under the row (we only annotate the standards case; a
 * name/description match is self-explanatory).
 */
export function isStandardOnlyMatch(rdef: RuleDef, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return false;
  const shape = parseIdQuery(trimmed);
  if (shape.kind !== 'none') return false;
  const needle = trimmed.toLowerCase();
  const name = (rdef.name ?? '').toLowerCase();
  const desc = (rdef.description ?? '').toLowerCase();
  const type = rdef.type.toLowerCase();
  if (name.includes(needle) || desc.includes(needle) || type.includes(needle)) {
    return false;
  }
  return ruleHasStandardMatch(rdef.type, trimmed);
}

/**
 * Compose a short "Matches: X, Y +N more" string for a rule that
 * matched on a security standard. Returns an empty string when the
 * match wasn't standards-sourced (callers can `?` on the return).
 * Caps the inlined list at 2 standards to keep the row small; a merchant
 * who needs the full list opens the rule to see the Standards section.
 */
export function matchedStandardsSummary(rdef: RuleDef, query: string): {
  readonly matched: readonly StandardRef[];
  readonly summary: string;
} {
  const matched = matchStandardsOnRule(rdef.type, query);
  if (matched.length === 0) {
    return { matched, summary: '' };
  }
  const first = matched.slice(0, 2).map((s) => s.label).join(', ');
  const remainder = matched.length - 2;
  const summary = remainder > 0 ? `Matches: ${first} +${remainder} more` : `Matches: ${first}`;
  return { matched, summary };
}

/**
 * Sort rule DEFS (catalog shape, no merchant rule_id) by their numeric
 * rule-id ascending. Mirrors `sortRulesByNumber` but keyed off the
 * catalog `type` only — non-numeric types sort stably after every
 * numbered rule.
 */
export function sortRuleDefsByNumber(defs: readonly RuleDef[]): RuleDef[] {
  return [...defs].sort((a, b) => {
    const na = extractRuleNumber(a.type);
    const nb = extractRuleNumber(b.type);
    if (na === null && nb === null) return a.type.localeCompare(b.type);
    if (na === null) return 1;
    if (nb === null) return -1;
    return na - nb;
  });
}

// ---------------------------------------------------------------------------
// Rendered controls — kept close to the state so a reader sees the shape
// (query / mode) alongside the UI consuming it.
// ---------------------------------------------------------------------------

export interface RuleFinderControlsProps {
  readonly C: ThemeTokens;
  readonly query: string;
  readonly mode: RuleFinderMode;
  readonly onQueryChange: (next: string) => void;
  readonly onModeChange: (next: RuleFinderMode) => void;
  readonly onClear: () => void;
}

/**
 * Search input + segmented view toggle. Layout mirrors the AttackersView /
 * Agents search chrome: 1px border, 2px radius, Geist / JetBrains Mono
 * typography. The toggle borrows the pattern from ViewToggle (lime active
 * segment, ink text) so a merchant sees the same affordance across views.
 */
export function RuleFinderControls({
  C,
  query,
  mode,
  onQueryChange,
  onModeChange,
  onClear,
}: RuleFinderControlsProps): JSX.Element {
  return (
    <div
      className="flex items-stretch gap-2 flex-wrap"
      data-testid="guardrails-rule-finder"
    >
      <div
        className="flex items-center flex-1"
        style={{
          minWidth: 220,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          minHeight: 36,
          paddingLeft: 10,
          paddingRight: 6,
        }}
      >
        <Search size={14} color={C.muted} aria-hidden="true" />
        <input
          type="text"
          inputMode="search"
          role="searchbox"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && query !== '') {
              e.preventDefault();
              onClear();
            }
          }}
          placeholder="Find a rule — r26, 26, refund…"
          aria-label="Find a rule"
          data-testid="guardrails-rule-finder-input"
          className="flex-1"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            marginLeft: 8,
            minWidth: 0,
            padding: '7px 0',
          }}
        />
        {query !== '' ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            data-testid="guardrails-rule-finder-clear"
            className="flex items-center justify-center"
            style={{
              background: 'transparent',
              border: 'none',
              color: C.muted,
              cursor: 'pointer',
              padding: 4,
              marginLeft: 4,
            }}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label="Rule list layout"
        data-testid="guardrails-rule-finder-mode"
        className="flex items-stretch"
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: C.surface,
        }}
      >
        {(
          [
            { value: 'category', label: 'By category' },
            { value: 'number', label: 'By number' },
          ] as const
        ).map((seg) => {
          const active = mode === seg.value;
          return (
            <button
              key={seg.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onModeChange(seg.value)}
              data-testid={`guardrails-rule-finder-mode-${seg.value}`}
              style={{
                minHeight: 36,
                minWidth: 44,
                padding: '0 12px',
                background: active ? C.lime : 'transparent',
                color: active ? C.ink : C.muted,
                fontFamily: sansFont,
                fontSize: 11,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface RuleFinderStatusLineProps {
  readonly C: ThemeTokens;
  readonly query: string;
  readonly matchCount: number;
}

/**
 * One quiet line beneath the search input. Rendered only when the query
 * is non-empty; carries either "N rules match" or
 * "No rule matches "<query>"." (always type-correct: a quotation-mark pair,
 * never an exclamation point — Sill voice).
 */
export function RuleFinderStatusLine({
  C,
  query,
  matchCount,
}: RuleFinderStatusLineProps): JSX.Element | null {
  const trimmed = query.trim();
  if (trimmed === '') return null;
  const body =
    matchCount === 0
      ? `No rule matches “${trimmed}”.`
      : `${matchCount} rule${matchCount === 1 ? '' : 's'} match`;
  return (
    <div
      className="mt-2"
      data-testid="guardrails-rule-finder-status"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1.5,
        color: C.muted,
        textTransform: 'uppercase',
      }}
    >
      {body}
    </div>
  );
}

export interface RuleCategoryTagProps {
  readonly C: ThemeTokens;
  readonly rule: Rule;
}

/**
 * Compact mono category label rendered on a rule row when it is being
 * shown outside its own category context ("By number" mode, or
 * cross-category search results). The dashboard already drops the
 * category pill inside a single-category view (UX review 2026-06-01), so
 * this component only renders when the surrounding list has decided the
 * row needs it.
 */
export function RuleCategoryTag({ C, rule }: RuleCategoryTagProps): JSX.Element | null {
  const cat = categoryById(rule.category);
  if (!cat) return null;
  return (
    <span
      data-testid="guardrails-rule-category-tag"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1,
        color: C.muted,
        border: `1px solid ${C.border}`,
        padding: '1px 6px',
        borderRadius: 2,
        whiteSpace: 'nowrap',
      }}
    >
      {cat.label.toUpperCase()}
    </span>
  );
}

